import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from './localeShared';

export const LOCALE_STORAGE_KEY = 'sim-locale';

export const normalizeLocale = (value) => {
  if (!value || typeof value !== 'string') {
    return DEFAULT_LOCALE;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('sv')) {
    return 'sv';
  }
  if (normalized.startsWith('en')) {
    return 'en';
  }
  return DEFAULT_LOCALE;
};

export const resolveLocale = (preferredLocale) => {
  if (preferredLocale) {
    return normalizeLocale(preferredLocale);
  }
  const stored = typeof localStorage !== 'undefined'
    ? localStorage.getItem(LOCALE_STORAGE_KEY)
    : null;
  if (stored) {
    return normalizeLocale(stored);
  }
  return DEFAULT_LOCALE;
};

export const applyLocale = (locale) => {
  const normalized = normalizeLocale(locale);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
  }
  return normalized;
};

/**
 * BCP-47 tag for display formatting. English uses en-GB so day comes before month.
 */
export const localeToBcp47 = (locale) =>
  normalizeLocale(locale) === 'sv' ? 'sv-SE' : 'en-GB';

const pad2 = (value) => String(value).padStart(2, '0');

const toValidDate = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * User-visible dates: DD/MM/YYYY in the local timezone.
 * Pass Intl `options` for long/short labels (still day-before-month via localeToBcp47).
 */
export const formatDate = (value, locale, options) => {
  const date = toValidDate(value);
  if (!date) {
    return '';
  }
  if (options) {
    return date.toLocaleDateString(localeToBcp47(locale), options);
  }
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
};

/**
 * User-visible datetimes: DD/MM/YYYY HH:mm (24-hour) in the local timezone.
 */
export const formatDateTime = (value, locale, options) => {
  const date = toValidDate(value);
  if (!date) {
    return '';
  }
  if (options) {
    return date.toLocaleString(localeToBcp47(locale), { hour12: false, ...options });
  }
  return `${formatDate(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/** Compact labels (e.g. dashboard charts): day before month, abbreviated month text. */
export const formatDateShort = (value, locale) =>
  formatDate(value, locale, { month: 'short', day: 'numeric' });

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
